// Layer 3 (Transformation): the selected stage's depth as one tab set
// (polish.P11, comp variant A). Two groups — the stage's reads (What is off ·
// Operating window · Displays · What good looks like) and its reference
// tables (Balance · Streams · Rules · Units · Headroom). Every count is the
// length of the rows its tab renders (adjudication 3), and a tab with no rows
// is not drawn (absence is never content).

import type { OffPane } from './Circuits.transform';
import type { Reading } from '@/versions/v1/schema/reading';
import type { ScenarioPhase, SessionState } from '@/versions/v1/schema/scenario';
import type { UnitHealth, UnitOperation } from '@/versions/v1/schema/unit-operation';
import { unitHealth, unitKeyParamLabel } from './Scene.transform';

export type StageTabId =
  | 'off'
  | 'window'
  | 'displays'
  | 'looks'
  | 'balance'
  | 'streams'
  | 'rules'
  | 'units'
  | 'headroom';

export type StageTab = { id: StageTabId; label: string; count?: string };

export type StageTabGroups = {
  reads: readonly StageTab[];
  reference: readonly StageTab[];
};

const PANE_WINDOW_LABEL: Record<OffPane['window'], string> = {
  '24h': 'last 24 h',
  '7d': 'last 7 d',
};

function counted(id: StageTabId, label: string, rows: number): StageTab[] {
  return rows > 0 ? [{ id, label, count: String(rows) }] : [];
}

export function stageTabs(input: {
  pane: OffPane | undefined;
  windowRows: number;
  displays: number;
  looksRows: number;
  maActive: boolean;
  streams: number;
  rules: number;
  units: number;
  headroom: number;
}): StageTabGroups {
  return {
    reads: [
      ...(input.pane ? [{ id: 'off' as const, label: 'What is off', count: PANE_WINDOW_LABEL[input.pane.window] }] : []),
      ...counted('window', 'Operating window', input.windowRows),
      ...counted('displays', 'Displays', input.displays),
      ...counted('looks', 'What good looks like', input.looksRows),
    ],
    reference: [
      ...(input.maActive ? [{ id: 'balance' as const, label: 'Balance' }] : []),
      ...counted('streams', 'Streams', input.streams),
      ...counted('rules', 'Rules', input.rules),
      ...counted('units', 'Units', input.units),
      ...counted('headroom', 'Headroom', input.headroom),
    ],
  };
}

/** The tab a stage opens on: the balance while the MA layer is on, what is off
 *  while the circuit drifts, else the operating window — or the first tab the
 *  stage has. A requested tab the stage still has is kept. */
export function resolveStageTab(groups: StageTabGroups, requested: StageTabId | null): StageTabId | null {
  const all = [...groups.reads, ...groups.reference];
  if (requested && all.some((tab) => tab.id === requested)) return requested;
  for (const preferred of ['balance', 'off', 'window'] as const) {
    if (all.some((tab) => tab.id === preferred)) return preferred;
  }
  return all[0]?.id ?? null;
}


export const UNIT_HEALTH_LABEL: Record<UnitHealth, string> = {
  ok: 'OK',
  warn: 'Watch',
  alarm: 'Alarm',
};

export type StageUnitRow = {
  unit: UnitOperation;
  health: UnitHealth;
  keyParam: string;
};

/** The Units tab's rows: each unit's health and key parameter at the phase. */
export function stageUnitRows(
  units: readonly UnitOperation[],
  input: { phase: ScenarioPhase; readings: readonly Reading[]; state: SessionState; nodeId: string },
): StageUnitRow[] {
  return units.map((unit) => ({
    unit,
    health: unitHealth(unit, input.phase, input.readings, input.state, input.nodeId),
    keyParam: unitKeyParamLabel(unit, input.phase, input.readings),
  }));
}

/** The throughput-vs-P80 curve's display y-domain: the engine's domain with
 *  its top rounded up to the next whole tick step, so the axis ends on a
 *  labelled line (74 · 80 · 85 · 90) instead of mid-step. No point moves. */
export function curveDisplayYDomain(domain: readonly [number, number], step = 5): [number, number] {
  return [domain[0], Math.ceil(domain[1] / step) * step];
}
